import { Box, Grid, Tbody, Text, Tfoot, Thead } from '@chakra-ui/react'
import type { InsuranceParamsGridItem } from 'BrokerTsLib/src/dtos'
import { Product } from 'BrokerTsLib/src/enums'
import { useContext, useMemo } from 'react'

import { contractsTravelContext } from '@/pages/travel/contracts'

import { contractsPropertyContext } from '@/pages/property/contracts'
import { useTranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'
import { Pagination, Table, TableData, TableHeader, TableRow } from '../../components/Grid'
import { Loading } from '../../components/Loading'
import { useApp } from '../../hooks/useApp'
import { contractsCarsContext } from '../../pages/vehicle/contracts'
import { ContractRow } from './ContractRow'

export const getContractsContext = (product: Product) => {
  switch (product) {
    case Product.TravelInsurance: return contractsTravelContext
    case Product.VehicleInsurance:
    case Product.FleetInsurance:
    case Product.VehicleLeasingInsurance: return contractsCarsContext
    case Product.LiabilityInsurance:
    case Product.PropertyInsurance: return contractsPropertyContext
    default: throw Error('Chybí property concepts context')
  }
}

export const TableWrapper = ({ children }: { children: React.ReactNode }) => {
  return <Box sx={{ maxW: '100%', overflow: 'hidden', overflowX: 'auto' }}>{children}</Box>
}

export const ContractsList = ({
  loading,
  contracts,
  withPaginator = false,
  leftFooterPart
}: {
  loading?: boolean
  contracts: InsuranceParamsGridItem[]
  withPaginator?: boolean
  leftFooterPart?: React.ReactNode
}) => {
  const { product } = useApp()
  const context = useMemo(() => getContractsContext(product!), [product])
  const { currentPage, pageSize, totalCount, setCurrentPage, reload, ...rest } = useContext(context)
  const { translate } = useTranslationsContext()
  loading = loading || rest.loading

  return (
    <Box sx={{ display: 'grid', gap: 4 }}>
      <TableWrapper>
        <Table cellPadding="0" cellSpacing="0">
          <Thead>
            <TableRow hoverEffect={false}>
              <TableHeader>
                <p>{translate('contractNumber', 'Číslo smlouvy')}</p>
              </TableHeader>

              <TableHeader textAlign='center'>
                <p>{translate('policyStatus', 'Stav smlouvy')}</p>
              </TableHeader>

              <TableHeader>
                <p>{translate('insuranceParamsId', 'Číslo nabídky')}</p>
              </TableHeader>

              <TableHeader>
                <p>{translate('dateContracted', 'Datum sjednání')}</p>
                <p>{translate('dateFrom', 'Datum počátku')}</p>
              </TableHeader>

              <TableHeader sx={{ px: 0 }}>{/* <!-- Space for initials --> */}</TableHeader>

              <TableHeader sx={{ pl: 0 }}>
                {product == Product.VehicleLeasingInsurance && <p>{translate('client', 'Klient')}</p>}
                {product != Product.VehicleLeasingInsurance && <p>{translate('policyholder', 'Pojistník')}</p>}
                {[Product.VehicleInsurance, Product.VehicleLeasingInsurance].includes(product!) && <p>{translate('vehicle', 'Vozidlo')}</p>}
              </TableHeader>

              <TableHeader>
              </TableHeader>

              <TableHeader>{translate('price', 'Cena')}</TableHeader>

              <TableHeader>
                <p>{translate('conceptName', 'Název nabídky')}</p>
                <p>{translate('processedBy', 'Zpracoval')}</p>
              </TableHeader>
            </TableRow>
          </Thead>

          <Tbody>
            {!loading && ((contracts?.length ?? 0) > 0) &&
              contracts.map((contract) => {
                return <ContractRow
                  key={`${contract.insuranceParamsId}_${contract.offerId}`}
                  item={contract} />
              })}

            {!loading && ((contracts?.length ?? 0) == 0) &&
              <TableRow>
                <TableData colSpan={9} py={8} textAlign={'center'}>
                  <Text>{translate('noData', 'Žádné smlouvy k zobrazení')}</Text>
                </TableData>
              </TableRow>
            }

            {loading &&
              <TableRow>
                <TableData colSpan={9} py={8}>
                  <Loading large={true} />
                </TableData>
              </TableRow>
            }
          </Tbody>

          {withPaginator && !loading &&
            <Tfoot>
              <TableRow hoverEffect={false}>
                <TableData colSpan={9}>

                  <Grid gridTemplateColumns='1fr auto 1fr'>
                    {leftFooterPart && leftFooterPart}
                    {!leftFooterPart && <Box></Box>}

                    {withPaginator && (
                      <Pagination totalCount={totalCount} currentPage={currentPage} setCurrentPage={setCurrentPage} maxButtonCount={10} pageSize={pageSize} />
                    )}
                  </Grid>
                </TableData>
              </TableRow>
            </Tfoot>}

        </Table>
      </TableWrapper >


    </Box>
  )
}
